import React, { useCallback } from 'react';

import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Box } from '@mui/material';
import { Catalogs } from '@europrocurement/l2d-domain';
import {
    PackagePackageRead,
    TblSurchargeArticlePackageJsonldCatalogueReadCatalogueReadGrilleTarifaire,
} from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { ModelForm } from '../../forms/types';
import { articlesStepSchema, ConfigurationSchema } from '../../Package/Form/validation';
import { apiArticlesToFormEditableArticles } from '../../Article/format';
import { SortableList, SortableItem } from '../../components/SortableList';
import ArticleLine from '../../Package/Components/ArticleLine';
import useArticlesModuleConstraints from '../../Package/Hook/useArticlesModuleConstraints';
import useOffreForm from '../../../GrilleTarifaire/hook/useOffreForm';

type FormProps = {
    entity: Catalogs;
    idCatalogue: number;
    idPrescriber: number;
    isCreate: boolean;
    afterSubmit: () => void;
};

export function getRelationIdFromArticleId(relations, articleId) {
    const foundRelation = relations.find((rel) => rel.article?.id === articleId);
    return foundRelation?.id ?? null;
}

export function appliquerSurchargesParPriorite(
    relationArticles: PackagePackageRead[],
    surchargeCatalogue: TblSurchargeArticlePackageJsonldCatalogueReadCatalogueReadGrilleTarifaire[] = [],
    surchargePrescripteur: TblSurchargeArticlePackageJsonldCatalogueReadCatalogueReadGrilleTarifaire[] = [],
) {
    return relationArticles.map((relation) => {
        const idArticle = relation.article?.id;
        const surcharge =
            surchargePrescripteur.find((s) => s.article?.article.id === idArticle) ??
            surchargeCatalogue.find((s) => s.relationArticle?.article?.id === idArticle);

        if (surcharge) {
            return {
                ...relation,
                tarif: surcharge.tarif,
            };
        }

        return relation;
    });
}

const PrescriberArticleOverrideForm: React.FunctionComponent<ModelForm<Articles>> = function ({
    entity,
    idCatalogue,
    idPrescriber,
    isCreate,
    afterSubmit,
}: FormProps) {
    // Form configuration

    /** Creation form | Default values  */
    const getDefaultValues = () => ({
        label: '',
        commercialName: '',
        domainId: 0,
        formalityId: null,
        articles: [],
        actions: [],
        additionalFields: [],
        documents: [],
        readOnlyDocuments: {
            articles: [],
            options: [],
            formalities: [],
        },
    });

    const domainId = entity?.package.domaine?.id ?? 0;

    const { getWarnings } = useArticlesModuleConstraints();
    const { createSurchargeArticle, updateSurchargeArticle, deleteSurchargeArticle } =
        useOffreForm();

    const getConfiguration = (): ConfigurationSchema => ({
        label: entity?.package.libelle ?? '',
        commercialName: entity?.package.nomCommercial ?? '',
        domainId,
        formalityId: entity?.package.formalite?.id,
    });

    const articlesVac =
        entity?.package?.articles.filter((item) => item.article?.rubArticle === 'VAC') ?? [];
    const listArticles = appliquerSurchargesParPriorite(
        articlesVac,
        entity.surchargeArticles,
        entity.surchargePrescripteur,
    );

    /** Update form | Default values from api  */
    const getPackageFromEntity = () => ({
        ...getConfiguration(),
        ...apiArticlesToFormEditableArticles(listArticles ?? []),
    });

    const formValues = isCreate ? getDefaultValues() : getPackageFromEntity();

    const formContext = useForm({
        mode: 'onBlur',
        defaultValues: { ...formValues },
        resolver: zodResolver(articlesStepSchema),
    });

    const { articles } = formContext.getValues() as PackageFormData;
    const setArticles = (values: ArticlesSchema['articles']) => {
        formContext.setValue('articles', values);
    };

    const treatmentArticle = useCallback(() => {
        const promises: Promises[] = articles
            .filter((item) => listArticles.some((rel) => rel.article?.id === item.articleId))
            .map((item) => {
                if (item.price === undefined || item.price === '0' || item.price === '') {
                    return undefined;
                }
                const relation = listArticles.find((rel) => rel.article?.id === item.articleId);
                const surchargeExist =
                    entity.surchargePrescripteur.find((s) => s.article?.id === relation.id) ??
                    undefined;
                if (surchargeExist) {
                    return updateSurchargeArticle(surchargeExist.id, {
                        article: relation.id,
                        catalogue: idCatalogue,
                        tarif: item.price,
                        prescripteur: idPrescriber,
                    });
                }
                return createSurchargeArticle({
                    article: relation.id,
                    catalogue: idCatalogue,
                    tarif: item.price,
                    prescripteur: idPrescriber,
                });
            });

        Promise.all(promises)
            .then(() => {
                afterSubmit();
            })
            .catch((err) => {
                console.log(err);
            });
    }, [
        afterSubmit,
        articles,
        listArticles,
        idCatalogue,
        idPrescriber,
        createSurchargeArticle,
        entity.surchargePrescripteur,
        updateSurchargeArticle,
    ]);

    const treatmentArticleDelete = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        const promises: Promises[] = entity.surchargePrescripteur.map((item) =>
            deleteSurchargeArticle(item.id),
        );
        Promise.all(promises)
            .then(() => {
                afterSubmit();
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const {
        formState: { errors },
        handleSubmit,
    } = formContext;
    const stringIndexes = Object.keys(errors.articles ?? {});
    const errorIndexes = stringIndexes.map((stringIndex) => parseInt(stringIndex, 10));

    const innerHandleSubmit = () => handleSubmit(treatmentArticle);

    return (
        <FormProvider {...formContext}>
            <form
                noValidate
                onSubmit={innerHandleSubmit()}
            >
                <SortableList
                    items={articles}
                    onChange={setArticles}
                    renderItem={(article: ArticleFormItem, index: number) => {
                        const hasErrorOnValidation = errorIndexes.includes(index);
                        return (
                            <SortableItem
                                id={article.id}
                                key={`package_content_item_${article.id}`}
                            >
                                <ArticleLine
                                    article={article}
                                    index={index}
                                    context={formContext}
                                    hasErrorOnValidation={hasErrorOnValidation}
                                    warnings={getWarnings(article.id, articles)}
                                    disabled
                                    isOptionForm={false}
                                />
                            </SortableItem>
                        );
                    }}
                />
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '8px',
                    }}
                >
                    <Button
                        type="submit"
                        variant="contained"
                    >
                        Valider
                    </Button>
                    <Button
                        onClick={treatmentArticleDelete}
                        variant="contained"
                        color="error"
                    >
                        Supprimer les surcharges
                    </Button>
                </Box>
            </form>
        </FormProvider>
    );
};

export default PrescriberArticleOverrideForm;
